"use client"
import { useRef, useState, useEffect } from "react"
import { motion } from "framer-motion"
import { childVariants } from "./motion"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function VerifyEmail({ setStep , tempToken}) {
  const inputRefs = useRef([])
  const [code, setCode] = useState(["", "", "", "", "", ""])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [validationError, setValidationError] = useState(null)
  const [resendDisabled, setResendDisabled] = useState(true)
  const [timer, setTimer] = useState(120)

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  useEffect(() => {
    let countdown
    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setResendDisabled(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => clearInterval(countdown)
  }, [timer])

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return
    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)
    setValidationError(null) // Clear validation error when user types

    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleResend = async () => {
    setError(null);
    setValidationError(null);
    setResendDisabled(true);
    setTimer(120);
  
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/resendCode`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${tempToken}`, // Add the Bearer token
        },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || "Failed to resend the code");
      }
  
      // console.log("Code resent successfully");
    } catch (error) {
      console.error("Error resending code:", error);
      setError(error.message || "Failed to resend the code. Please try again.");
      setResendDisabled(false);
      setTimer(0);
    }
  };
  
  const validateCode = () => {
    const joinedCode = code.join("")
    if (joinedCode.length === 0) {
      setValidationError("Please enter the verification code")
      return false
    }
    if (joinedCode.length !== 6) {
      setValidationError("Please enter all 6 digits of the code")
      return false
    }
    if (!/^\d{6}$/.test(joinedCode)) {
      setValidationError("Code must contain 6 digits")
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    const joinedCode = code.join("")
    setError(null)
    setValidationError(null)

    if (!validateCode()) {
      return
    }

    setLoading(true)

    if (!tempToken) {
      setLoading(false)
      setError("Authentication token not found.")
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/verifyEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tempToken}`,
        },
        body: JSON.stringify({ code: joinedCode }),
      })

      const result = await response.json()

      if (response.ok) {
        setStep(3)
      } else {
        console.error("Error verifying email:", result)
        setError(result.message || "Failed to verify email.")
      }
    } catch (error) {
      console.error("Unexpected error:", error)
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full mx-2 md:w-[600px] px-6 md:px-12 py-8 space-y-6 bg-white shadow-custom rounded-lg">
      <div className="text-start space-y-2">
        <h2 className="text-xl text-gray-800">Enter Code for account verification</h2>
        <p className="text-sm text-gray-600">You will receive a 6-digit code via email</p>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        className="flex justify-center gap-2"
      >
        {code.map((digit, index) => (
          <motion.div key={index} variants={childVariants} className="relative">
            <input
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="h-10 w-10 md:w-14 md:h-14 text-center text-2xl md:text-4xl font-normal p-0 border-0 border-b-2 md:border-b-4 border-gray-300 outline-none focus:border-blue-500 transition-colors rounded-sm"
              aria-label={`Digit ${index + 1} of verification code`}
            />
          </motion.div>
        ))}
      </motion.div>

      <div className="space-y-4">
        <div className="flex justify-center items-center">
          {loading ? (
            <button
              className="w-full px-4 py-2 text-white bg-[#036494] rounded-full h-[40px] flex items-center justify-center"
              disabled
            >
              <span className="loader"></span>
            </button>
          ) : (
            <button onClick={handleSubmit} className="w-full mx-4 py-2 text-white bg-[#036494] rounded-full">
              Enter code
            </button>
          )}
        </div>

        {validationError && <p className="text-center text-red-600 text-sm">{validationError}</p>}

        {error && <p className="text-center text-red-600 text-sm">{error}</p>}

        <p className="text-center text-sm text-gray-600">
          If code is not received in {formatTime(timer)} minute, click{" "}
          <button
            onClick={handleResend}
            disabled={resendDisabled}
            className={`${
              resendDisabled ? "text-gray-400 cursor-not-allowed" : "text-blue-600 hover:underline focus:outline-none"
            }`}
          >
            Resend
          </button>{" "}
          to get another code.
        </p>
      </div>
    </div>
  )
}